import React, { ReactNode, useCallback, useEffect, useMemo, useReducer } from 'react';
import type { AuthOption } from 'sendbird-calls';
import SendbirdCall, { LoggerLevel, RoomType } from 'sendbird-calls';

import CallContext, { initialContext } from './context';
import type { ContextType } from './context';
import { reducer } from './reducer';
import { initialState } from './state';
import { statefyRoom } from './statefy';

/**
 * Provider
 * ```tsx
 * <SbCallsProvider>
 *   <MyApp />
 * </Auth0Provider>
 * ```
 *
 * Provides the SbCallsProvider to its child components.
 */
const SbCallsProvider = ({ appId, children }: { appId: string; children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { calls } = state;
  const currentCall = useMemo(() => calls.find((call) => !call.isEnded), [calls]);
  const isBusy = useMemo(() => calls.some((call) => !call.isEnded), [calls]);

  const init = useCallback<ContextType['init']>((nAppId) => {
    const listenerId = 'device-change-listener';
    try {
      SendbirdCall.removeListener(listenerId);
    } catch (error) {
      // nothing
    }

    SendbirdCall.init(nAppId);
    SendbirdCall.setLoggerLevel(LoggerLevel.ERROR);
    SendbirdCall.addListener(listenerId, {
      onAudioInputDeviceChanged: (current, available) => {
        dispatch({ type: 'UPDATE_AUDIO_INPUT_DEVICE_INFO', payload: { current, available } });
      },
      onAudioOutputDeviceChanged: (current, available) => {
        dispatch({ type: 'UPDATE_AUDIO_OUTPUT_DEVICE_INFO', payload: { current, available } });
      },
      onVideoInputDeviceChanged: (current, available) => {
        dispatch({ type: 'UPDATE_VIDEO_INPUT_DEVICE_INFO', payload: { current, available } });
      },
      onInvitationReceived: async (invitation) => {
        try {
          await invitation.accept();
          const room = await fetchRoomById(invitation.room.roomId);
          await room.enter({ audioEnabled: true, videoEnabled: true, kickSiblings: true });
        } catch (error) {
          console.log('event', error);
        }
      },
    });

    SendbirdCall.updateMediaDevices({ audio: true, video: true });
  }, []);

  useEffect(() => {
    if (appId) init(appId);
  }, [appId]);

  // Tear the SDK down on provider unmount so the WebSocket + listeners
  // don't keep chatting with Sendbird after the user leaves the call page.
  useEffect(() => {
    return () => {
      try {
        SendbirdCall.removeAllListeners();
        // deauthenticate() returns void; the setSinkId NotAllowedError its internal room
        // teardown can raise is unreachable from here and is filtered in Sentry.init (pages/_app.js).
        SendbirdCall.deauthenticate();
      } catch (err) {
        console.error(err);
      }
    };
  }, []);

  const ringingListenerId = 'sb-call-listener';
  const auth = useCallback(async (authOption: AuthOption) => {
    const user = await SendbirdCall.authenticate(authOption);
    SendbirdCall.addListener(ringingListenerId, {
      // onAudioInputDeviceChanged: (current, available) => {},
      // onAudioOutputDeviceChanged: (current, available) => {},
      // onVideoInputDeviceChanged: (current, available) => {},
    });
    await SendbirdCall.connectWebSocket();

    dispatch({ type: 'AUTH', payload: user });
    return user;
  }, []);

  const deauth = useCallback<ContextType['deauth']>(() => {
    SendbirdCall.removeListener(ringingListenerId);
    SendbirdCall.deauthenticate();
    dispatch({ type: 'DEAUTH' });
  }, []);

  /*
    Media Device Control
   */
  const updateMediaDevices = useCallback<ContextType['updateMediaDevices']>((constraints) => {
    SendbirdCall.updateMediaDevices(constraints);
  }, []);

  const selectAudioInputDevice = useCallback<ContextType['selectAudioInputDevice']>(
    (mediaInfo: InputDeviceInfo) => {
      SendbirdCall.selectAudioInputDevice(mediaInfo);
      dispatch({ type: 'UPDATE_AUDIO_INPUT_DEVICE_INFO', payload: { current: mediaInfo } });
    },
    []
  );

  const selectAudioOutputDevice = useCallback<ContextType['selectAudioOutputDevice']>(
    (mediaInfo: MediaDeviceInfo) => {
      SendbirdCall.selectAudioOutputDevice(mediaInfo);
      dispatch({ type: 'UPDATE_AUDIO_OUTPUT_DEVICE_INFO', payload: { current: mediaInfo } });
    },
    []
  );

  const selectVideoInputDevice = useCallback<ContextType['selectVideoInputDevice']>(
    (mediaInfo: InputDeviceInfo) => {
      SendbirdCall.selectVideoInputDevice(mediaInfo);
      dispatch({ type: 'UPDATE_VIDEO_INPUT_DEVICE_INFO', payload: { current: mediaInfo } });
    },
    []
  );

  /*
    Rooms
   */
  const createRoom = useCallback<ContextType['createRoom']>(async (options) => {
    const room = await SendbirdCall.createRoom(options);
    const statefulRoom = statefyRoom(room, dispatch);
    dispatch({ type: 'ADD_ROOM', payload: statefulRoom });
    return statefulRoom;
  }, []);

  const getCachedRoomById = useCallback<ContextType['getCachedRoomById']>(
    (roomId) => {
      return state.rooms.find((x) => x.roomId === roomId);
    },
    [state.rooms]
  );

  const fetchRoomById = useCallback<ContextType['fetchRoomById']>(
    async (roomId) => {
      const room = await SendbirdCall.fetchRoomById(roomId);
      const statefulRoom = statefyRoom(room, dispatch);
      if (state.rooms.find((x) => x.roomId === room.roomId)) {
        dispatch({ type: 'UPDATE_ROOM', payload: statefulRoom });
      } else {
        dispatch({ type: 'ADD_ROOM', payload: statefulRoom });
      }
      return statefulRoom;
    },
    [state.rooms]
  );
  const callContext: ContextType = {
    ...initialContext,
    ...state,
    init,
    auth,
    deauth,
    isAuthenticated: !!state.user,

    // Media Device Control
    updateMediaDevices,
    selectAudioInputDevice,
    selectAudioOutputDevice,
    selectVideoInputDevice,

    // Direct Calls
    currentCall,
    isBusy,
    addDirectCallSound: SendbirdCall.addDirectCallSound,

    // Rooms
    createRoom,
    getCachedRoomById,
    fetchRoomById,
    RoomType,
  };
 
  return <CallContext.Provider value={callContext}>{children}</CallContext.Provider>;
};

export default SbCallsProvider;
